import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AuthContext from '../components/shared/AuthContext';
import {
  invalidateRealtime,
  notifyRealtimeUpdated,
  subscribeRealtimeInvalidate,
} from './realtimeBus';

const LearningRealtimeContext = createContext(null);

const DEFAULT_INTERVAL_MS = 30_000;
const MIN_REFRESH_GAP_MS = 2000;

export function LearningRealtimeProvider({ children, intervalMs = DEFAULT_INTERVAL_MS }) {
  const { apiClient, isAuthenticated, isLoading: authLoading } = useContext(AuthContext);
  const [learning, setLearning] = useState(null);
  const [usage, setUsage] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const lastFetchRef = useRef(0);
  const mountedRef = useRef(true);

  const applySnapshot = useCallback((data) => {
    if (!data || !mountedRef.current) return;
    setLearning(data.learning || {});
    setUsage(data.usage || null);
    setGamification(data.gamification || null);
    setServerTime(data.serverTime || null);
    setUpdatedAt(new Date());
    setError('');
    notifyRealtimeUpdated(data);
  }, []);

  const refresh = useCallback(
    async (silent = false) => {
      if (!isAuthenticated || authLoading) return;
      const now = Date.now();
      if (now - lastFetchRef.current < MIN_REFRESH_GAP_MS) return;
      lastFetchRef.current = now;

      if (!silent) setRefreshing(true);
      try {
        const res = await apiClient.get('/api/realtime/snapshot', silent ? { timeout: 25000 } : undefined);
        applySnapshot(res.data);
      } catch (e) {
        if (!mountedRef.current) return;
        const status = e.response?.status;
        if (status === 404) {
          const [learningRes, usageRes, gamificationRes] = await Promise.all([
            apiClient.get('/api/learning/stats'),
            apiClient.get('/api/users/me/usage').catch(() => ({ data: null })),
            apiClient.get('/api/gamification/summary').catch(() => ({ data: null })),
          ]);
          applySnapshot({
            learning: learningRes.data,
            usage: usageRes.data,
            gamification: gamificationRes.data,
            serverTime: new Date().toISOString(),
          });
          return;
        }
        setError(e.response?.data?.message || 'Live sync unavailable');
      } finally {
        if (mountedRef.current) setRefreshing(false);
      }
    },
    [apiClient, isAuthenticated, authLoading, applySnapshot]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      setLearning(null);
      setUsage(null);
      setGamification(null);
      return undefined;
    }

    refresh(true);
    const pollId = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    let invalidateTimer;
    const unsubInvalidate = subscribeRealtimeInvalidate((detail) => {
      if (detail?.reason === 'team-assignment-progress') {
        return;
      }
      window.clearTimeout(invalidateTimer);
      invalidateTimer = window.setTimeout(() => refresh(true), 1500);
    });

    return () => {
      window.clearInterval(pollId);
      window.clearTimeout(invalidateTimer);
      document.removeEventListener('visibilitychange', onVisible);
      unsubInvalidate();
    };
  }, [isAuthenticated, authLoading, intervalMs, refresh]);

  const value = useMemo(
    () => ({
      learning,
      usage,
      gamification,
      serverTime,
      updatedAt,
      refreshing,
      error,
      refresh,
      invalidate: invalidateRealtime,
      dueCount: Number(learning?.dueCount ?? 0),
      savedCount: Number(learning?.savedCount ?? 0),
      reviewedToday: Number(learning?.reviewedToday ?? 0),
    }),
    [learning, usage, gamification, serverTime, updatedAt, refreshing, error, refresh]
  );

  return (
    <LearningRealtimeContext.Provider value={value}>
      {children}
    </LearningRealtimeContext.Provider>
  );
}

export function useLearningRealtime() {
  const ctx = useContext(LearningRealtimeContext);
  return ctx;
}

export default LearningRealtimeProvider;
